import React, { useState } from 'react';
import { combineClasses } from '../../utils/formatters';
import {
  SPECIAL_OFFER_UI,
  SPECIAL_OFFER_DECOR,
  toOfferDisplayDate,
  getOfferTimeLeft
} from '../../config/specialOfferContent';

const SpecialOfferCard = ({ offer, onBookNow }) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleBookNow = () => {
    if (onBookNow) onBookNow(offer);
  };

  const handleViewDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleViewAllFestivals = () => {
    console.log('View all festivals clicked');
    // Navigate to festivals page
  };

  const timeLeftLabel = getOfferTimeLeft(offer.validUntil);
  const validUntilLabel = offer.validUntilFormatted || toOfferDisplayDate(offer.validUntil);

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Main Offer Card */}
      <div className={combineClasses(
        `bg-gradient-to-br ${offer.backgroundColor}`,
        "text-white relative overflow-hidden"
      )}>
        {/* Background Decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 right-4 text-8xl">{offer.icon}</div>
          <div className="absolute bottom-4 left-4 text-6xl rotate-12">{SPECIAL_OFFER_DECOR.emojis[0]}</div>
          <div className="absolute top-1/2 left-1/4 text-4xl">{SPECIAL_OFFER_DECOR.emojis[1]}</div>
          <div className="absolute bottom-1/3 right-1/3 text-5xl">{SPECIAL_OFFER_DECOR.emojis[2]}</div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {SPECIAL_OFFER_DECOR.bubbles.map((bubble) => (
            <div key={bubble.className} className={`absolute ${bubble.className} bg-white/10 rounded-full animate-pulse`}></div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            {/* Left Side - Content */}
            <div className="flex-1 max-w-2xl">
              {/* Urgency Badge */}
              <div className="inline-flex items-center mb-4">
                <span className="bg-red-500/20 text-red-100 px-3 py-1 rounded-full text-sm font-medium border border-red-300/30">
                  {SPECIAL_OFFER_UI.urgencyPrefix} {offer.urgency}
                </span>
                <span className="ml-3 bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {SPECIAL_OFFER_UI.timePrefix} {timeLeftLabel}
                </span>
              </div>

              {/* Title and Subtitle */}
              <h2 className="text-3xl sm:text-4xl font-bold mb-2 leading-tight">
                {offer.title}
              </h2>
              <p className="text-xl text-purple-100 mb-4 font-medium">
                {offer.subtitle}
              </p>
              <p className="text-purple-100 mb-6 text-lg leading-relaxed">
                {offer.detailedDescription}
              </p>

              {/* Discount Highlight */}
              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-3xl font-bold">
                      {offer.discount}% OFF
                    </div>
                    <div className="text-purple-100 text-sm">
                      {offer.savings}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-purple-200 text-sm line-through">
                      {offer.originalPrice}
                    </div>
                    <div className="text-xl font-bold">
                      {offer.offerPrice}
                    </div>
                  </div>
                </div>
                
                {/* Included Services Preview */}
                <div className="border-t border-white/20 pt-3">
                  <div className="text-sm text-purple-100 mb-2">Includes:</div>
                  <div className="flex flex-wrap gap-2">
                    {offer.includedServices.slice(0, 3).map((service, index) => (
                      <span key={index} className="bg-white/10 text-white px-2 py-1 rounded text-xs">
                        {service}
                      </span>
                    ))}
                    {offer.includedServices.length > 3 && (
                      <span className="bg-white/10 text-white px-2 py-1 rounded text-xs">
                        +{offer.includedServices.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={handleBookNow}
                  className="flex-1 bg-white text-purple-600 px-8 py-4 font-bold text-lg rounded-xl hover:bg-purple-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <span className="flex items-center justify-center">
                    <span className="mr-2">🚀</span>
                    {offer.cta}
                  </span>
                </button>
                <button 
                  onClick={handleViewAllFestivals}
                  className="flex-1 bg-white/20 backdrop-blur-sm text-white px-6 py-4 font-semibold rounded-xl hover:bg-white/30 transition-all duration-200 border border-white/30"
                >
                  {offer.secondaryCta || SPECIAL_OFFER_UI.secondaryFallbackCta}
                </button>
              </div>

              {/* Details Toggle */}
              <button
                onClick={handleViewDetails}
                className="mt-4 text-purple-100 hover:text-white text-sm flex items-center transition-colors"
              >
                <span className="mr-1">
                  {showDetails ? SPECIAL_OFFER_UI.detailToggleHide : SPECIAL_OFFER_UI.detailToggleShow}
                </span>
                <svg 
                  className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Right Side - Visual Element */}
            <div className="flex-shrink-0 relative">
              <div className="w-32 h-32 sm:w-40 sm:h-40 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                <span className="text-6xl sm:text-7xl">{offer.icon}</span>
              </div>
              <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                {SPECIAL_OFFER_UI.hotLabel}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Details Section */}
      {showDetails && (
        <div className="bg-white border-x-2 border-b-2 border-purple-200 p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Features */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">✨</span>
                {SPECIAL_OFFER_UI.includedHeading}
              </h4>
              <div className="space-y-2">
                {offer.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-3">✓</span>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Terms & Conditions */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📋</span>
                {SPECIAL_OFFER_UI.termsHeading}
              </h4>
              <div className="space-y-2">
                {offer.termsAndConditions.map((term, index) => (
                  <div key={index} className="flex items-start text-gray-600">
                    <span className="text-gray-400 mr-3 mt-1">•</span>
                    <span className="text-sm">{term}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Valid Until Notice */}
          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center text-orange-800">
              <span className="mr-2">⏰</span>
              <span className="font-medium">
                {SPECIAL_OFFER_UI.validUntilPrefix} {validUntilLabel}. {SPECIAL_OFFER_UI.validUntilSuffix}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialOfferCard;
